import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import HireportPagination from '@/UI/HireportPagination';

import { DEFAULT_LISTING_PAGE_SIZE } from './listingDataSource';

export interface SmartPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** When set, page numbers above this are disabled (cursor pagination). */
  maxReachablePage?: number;
  showInfo?: boolean;
  totalItems?: number;
  itemsPerPage?: number;
  /** Smaller padding for nested contexts (e.g. list card members). */
  compact?: boolean;
  /** Optional page-size selector (antd Pagination size changer). */
  showSizeChanger?: boolean;
  pageSizeOptions?: string[];
  onPageSizeChange?: (pageSize: number) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

const SmartPagination: React.FC<SmartPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxReachablePage,
  showInfo = false,
  totalItems = 0,
  itemsPerPage = DEFAULT_LISTING_PAGE_SIZE,
  compact = false,
  showSizeChanger = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
}) => {
  const [t] = useTranslation();

  // Clamp page count so antd disables next/prev at maxReachablePage itself.
  const paginationTotal =
    maxReachablePage != null
      ? maxReachablePage * itemsPerPage
      : totalItems > 0
        ? totalItems
        : totalPages * itemsPerPage;

  const renderPaginationItem = useCallback<
    NonNullable<React.ComponentProps<typeof HireportPagination>['itemRender']>
  >((page, type, originalElement) => {
    if (type === 'next' && React.isValidElement(originalElement)) {
      return React.cloneElement(
        originalElement as React.ReactElement<Record<string, unknown>>,
        {
          'data-testid': 'filtered-listing-pagination-next',
        },
      );
    }
    return originalElement;
  }, []);

  // Keep the bar visible when size changing is enabled so users can still
  // adjust page size on a single-page result set.
  if (totalPages <= 1 && !showSizeChanger) return null;

  return (
    <HireportPagination
      compact={compact}
      current={currentPage}
      pageSize={itemsPerPage}
      total={paginationTotal}
      showSizeChanger={showSizeChanger}
      pageSizeOptions={pageSizeOptions}
      showTotal={
        showInfo && totalItems > 0
          ? (_total, range) =>
              t('PAGINATION_INFO', {
                start: range[0],
                end: range[1],
                total: totalItems,
              })
          : undefined
      }
      onChange={(page, size) => {
        if (maxReachablePage != null && page > maxReachablePage) return;
        if (size !== itemsPerPage) {
          onPageSizeChange?.(size);
          onPageChange(1);
          return;
        }
        onPageChange(page);
      }}
      itemRender={renderPaginationItem}
      bordered={false}
    />
  );
};

export default SmartPagination;
